/** @jsxImportSource @emotion/react */
import React from 'react';
import { Card, Descriptions, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { ExperimentOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { styles } from '../styles';
import type { BbchInfo } from '../../../../types/simulation';
import { isDisplayableBbch, renderBbchBadge } from './LifecycleProgressCard';

const { Text } = Typography;

interface Props {
  simSessionId: string | null;
  simDisplayDate: string | null;
  loadingAttributes: boolean;
  displayedAttributes: any[];
  simulatedAgeDisplayUnit: 'days' | 'years' | null;
  simulatedAgeDisplayValue: number | null;
  simulatedAgeDisplayDateLabel: string | null;
  bbch?: BbchInfo | null;
}

const formatBbchDetails = (bbch?: BbchInfo | null): string | null => {
  if (!isDisplayableBbch(bbch)) return null;
  return [bbch?.principalLabel, bbch?.label].filter(Boolean).join(' · ') || bbch?.codeText || null;
};

const PropertiesCard: React.FC<Props> = ({
  simSessionId,
  simDisplayDate,
  loadingAttributes,
  displayedAttributes,
  simulatedAgeDisplayUnit,
  simulatedAgeDisplayValue,
  simulatedAgeDisplayDateLabel,
  bbch,
}) => {
  const hasBbch = isDisplayableBbch(bbch);
  const bbchDetails = formatBbchDetails(bbch);

  return (
    <Card
      title={
        <Space>
          <span>Сведения</span>
          {simSessionId && <Tag color="purple" icon={<ExperimentOutlined />}>Симуляция</Tag>}
          <Tooltip title={simSessionId
            ? "Отображаются расчётные значения из активной симуляции. Реальные данные станут доступны после завершения или отмены симуляции."
            : "Измеряемые параметры данного экземпляра (высота, здоровье, состояние и т.д.). Отображаются последние записанные значения из событий."
          }>
            <QuestionCircleOutlined css={styles.hintIcon} />
          </Tooltip>
        </Space>
      }
    >
      {loadingAttributes && <Spin />}
      {!loadingAttributes && displayedAttributes.length === 0 && !hasBbch && (
        <Text type="secondary">Измерений пока нет. Нажмите «Действие» для записи.</Text>
      )}
      {!loadingAttributes && (displayedAttributes.length > 0 || hasBbch) && (
        <Descriptions column={1} bordered size="small">
          {hasBbch && (
            <Descriptions.Item label={<Text strong>Фаза BBCH</Text>}>
              <Space wrap>
                {renderBbchBadge(bbch)}
                {bbchDetails && <Text>{bbchDetails}</Text>}
              </Space>
            </Descriptions.Item>
          )}
        {displayedAttributes.map((attr) => {
          const isAgeProperty = attr.propertyCode === 'age_days' || attr.propertyCode === 'age_years';
          let effectiveValueNumber = attr.valueNumber;
          let effectivePropertyName = attr.propertyName;
          let effectiveUnit = attr.unit;

          if (simSessionId && (attr.propertyCode === 'age_years' || attr.propertyCode === 'age_days')) {
            if (simulatedAgeDisplayUnit === 'days' && simulatedAgeDisplayValue !== null) {
              effectiveValueNumber = Math.round(simulatedAgeDisplayValue);
              effectivePropertyName = 'Возраст (дней)';
              effectiveUnit = 'дн';
            } else if (simulatedAgeDisplayUnit === 'years' && simulatedAgeDisplayValue !== null) {
              effectiveValueNumber = Number(simulatedAgeDisplayValue.toFixed(2));
              effectivePropertyName = 'Возраст (лет)';
              effectiveUnit = 'лет';
            }
          }

          return (
            <Descriptions.Item
              key={`${attr.propertyId}-${attr.updatedAt}`}
              label={
                <Space>
                  <Text strong>{effectivePropertyName}</Text>
                  {effectiveUnit && <Text type="secondary">({effectiveUnit})</Text>}
                </Space>
              }
            >
              <Space>
                {effectiveValueNumber !== null && <Tag color="blue">{effectiveValueNumber}</Tag>}
                {attr.valueText !== null && <Tag color="green">{attr.valueText}</Tag>}
                {attr.valueBoolean !== null && (
                  <Tag color={attr.valueBoolean ? 'success' : 'default'}>
                    {attr.valueBoolean ? 'Да' : 'Нет'}
                  </Tag>
                )}
              </Space>
              <Space>
                <Text type="secondary" css={styles.smallText}>
                  {simSessionId
                    ? ((isAgeProperty && simulatedAgeDisplayDateLabel)
                      ? simulatedAgeDisplayDateLabel
                      : (simDisplayDate
                        ? `${dayjs(simDisplayDate).format('DD.MM.YYYY')} · симуляция`
                        : 'симуляция'))
                    : dayjs(attr.updatedAt).format('DD.MM.YYYY HH:mm')}
                </Text>
              </Space>
            </Descriptions.Item>
          );
        })}
        </Descriptions>
      )}
    </Card>
  );
};

export default PropertiesCard;
